/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/*
 * InteragencyAnnualPass.jsx
 * Created by Destin Frasier on 03/23/23
 */

import React from 'react';
import {
    ContentContainerBodyShift,
    Heading,
    TextBlock,
    FlexRow,
    FlexCol,
    Button,
    Icon,
    ExternalLink,
} from 'sarsaparilla';

export default function InteragencyAnnualPass() {
    const imageSpecs = [
        {
            size: 'lg',
            width: '1440w',
        },
        {
            size: 'md',
            width: '800w',
        },
        {
            size: 'lg',
            width: '600w',
        },
    ];

    // Pass Image Info
    const pictureSource = (imageFormat) => {
        const path = process?.env?.CDN?.includes('/pass')
            ? process.env.CDN.replace('/pass', '')
            : process.env.CDN;
        const srcImage = () =>
            imageSpecs.map((imageSpec) => {
                return `${path}/img/interagency-annual-pass/all_cards_${imageSpec.size}.${imageFormat} ${imageSpec.width}`;
            });

        return imageFormat === 'png' ? (
            <img
                alt="The America the Beautiful Interagency Annual Passes"
                src={`${path}/img/interagency-annual-pass/all_cards_md.${imageFormat}`}
                srcSet={`${srcImage()}`}
            />
        ) : (
            <source
                type={`image/${imageFormat}`}
                sizes="100vw"
                srcSet={`${srcImage()}`}
            />
        );
    };

    const gaClickTagCategory = 'Interagency Passes Redirect';

    return (
        <div className="nav-interagency-annual-pass-wrap" role="main">
            <ContentContainerBodyShift className="mt-5">
                <FlexRow>
                    <FlexCol className="nav-interagency-annual-pass-back-row">
                        <Button
                            appearance="link"
                            href="/pass"
                            iconBeforeElement={<Icon iconName="arrow-back" />}
                            clickTagCategory={gaClickTagCategory}
                            clickTagAction="Back to Passes Home Button"
                            clickTagLabel="back button"
                        >
                            Back to Passes Home Page
                        </Button>
                        <span className="nav-interagency-annual-pass-revised-text">
                            Content Updated - March 2023
                        </span>
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol md={8}>
                        <Heading headingLevel={1} appearance="h4" className="mb-2">
                            Purchase Interagency Passes through the USGS Online Store
                        </Heading>
                        <TextBlock width="xl" className="mb-4">
                            <p>
                                The Interagency Annual Pass is no longer available for
                                purchase through Recreation.gov.
                            </p>
                            <ul>
                                <li>
                                    If you would like an interagency pass mailed to your
                                    home before your trip, please visit the{' '}
                                    <ExternalLink
                                        url="https://store.usgs.gov/recreational-passes"
                                        target="_blank"
                                        alt="Visit USGS Store"
                                        rel="noopener noreferrer"
                                        hasExternalIcon
                                        clickTagCategory={gaClickTagCategory}
                                        clickTagAction="USGS Store Button"
                                        clickTagLabel="main paragraph"
                                    >
                                        USGS Store
                                    </ExternalLink>{' '}
                                    to explore and select your pass options. Please
                                    consider the start date of your trip and factor in a
                                    minimum of 3 weeks to allow for processing and
                                    handling of the pass.
                                </li>
                                <li>
                                    If you would like to get your pass in person, please
                                    search the list of{' '}
                                    <ExternalLink
                                        url="https://www.nps.gov/planyourvisit/pickup-pass-locations.htm"
                                        target="_blank"
                                        alt="Locations where you can buy interagency passes"
                                        rel="noopener noreferrer"
                                        hasExternalIcon
                                        clickTagCategory={gaClickTagCategory}
                                        clickTagAction="Places to Get Interagency Passes Button"
                                        clickTagLabel="main paragraph"
                                    >
                                        Places to Get Interagency Passes
                                    </ExternalLink>
                                    .
                                </li>
                            </ul>
                        </TextBlock>
                    </FlexCol>
                    <FlexCol md={4} className="nav-interagency-annual-pass-image-wrap">
                        <picture>
                            {/* Show the hero image as an .avif */}
                            {pictureSource('avif')}

                            {/* If the browser can't handle an .avif, try a .webP */}
                            {pictureSource('webp')}

                            {/* If the browser can't handle an .avif or .webP, go with .png */}
                            {pictureSource('png')}
                        </picture>
                    </FlexCol>
                </FlexRow>
            </ContentContainerBodyShift>
        </div>
    );
}
